import React from 'react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { RsvpForm } from '@/src/modules/rsvp';
import { parseFlexibleDate } from '@/src/infrastructure/lib/weddingDataHelper';

const META: TemplateMetadata = {
  id: 'seed-classic-birthday',
  name: 'Classic Birthday',
  occasionType: 'BIRTHDAY',
  description: 'Vibrant confetti-inspired celebration layout.',
  isPremium: false,
  tags: ['fun', 'colorful', 'party'],
  colorScheme: { primary: '#7c3aed', accent: '#facc15', bg: '#fdf4ff' },
};

function fmt(iso: string | undefined): string {
  if (!iso) return '';
  const d = parseFlexibleDate(iso);
  if (!d) return iso;
  try {
    return new Intl.DateTimeFormat('en-IN', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    }).format(d);
  } catch {
    return iso;
  }
}

const DOTS = ['bg-yellow-400', 'bg-pink-400', 'bg-purple-400', 'bg-blue-400', 'bg-green-400'];

export function BirthdayFun({ data }: { data: InvitationData }) {
  const customDataAny = (data.customData as any) || {};
  const [firstMedia, ...gallery] = data.mediaUrls ?? [];
  const hero = firstMedia || customDataAny.heroImageUrl || customDataAny.photoUrl || customDataAny.birthday?.photo;
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';

  return (
    <div className="min-h-screen bg-purple-50 font-sans flex items-center justify-center p-8">
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-xl overflow-hidden">
        {/* confetti bar */}
        <div className="flex h-4">
          {DOTS.map((cls, i) => (
            <div key={i} className={`flex-1 ${cls}`} />
          ))}
        </div>

        <div className="px-10 py-12 text-center">
          {/* badge */}
          <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-700 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-8 border border-yellow-200">
            🎉 You're Invited!
          </div>

          <p className="text-purple-400 font-medium mb-2">Join us to celebrate</p>
          <h1 className="text-5xl font-extrabold text-purple-800 leading-tight mb-2">
            {data.eventName}
          </h1>
          <p className="text-stone-500 text-sm mb-8">
            Hosted by <span className="font-semibold text-purple-600">{data.hostName}</span>
          </p>

          {/* hero */}
          {hero && (
            <div className="w-full aspect-video rounded-2xl overflow-hidden mb-8 border-4 border-purple-100">
              <img
                src={hero}
                alt=""
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=500';
                }}
              />
            </div>
          )}

          {/* info cards */}
          <div className="grid grid-cols-2 gap-4 mb-10">
            <div className="bg-purple-50 rounded-2xl p-5 border border-purple-100 text-left">
              <p className="text-xs text-purple-400 uppercase tracking-widest mb-1">📅 When</p>
              <p className="text-purple-800 font-semibold text-sm leading-snug">
                {fmt(data.eventDate)}
              </p>
            </div>
            <div className="bg-pink-50 rounded-2xl p-5 border border-pink-100 text-left">
              <p className="text-xs text-pink-400 uppercase tracking-widest mb-1">📍 Where</p>
              <p className="text-pink-800 font-semibold text-sm leading-snug">
                {data.eventVenue}
              </p>
            </div>
          </div>

          {/* message */}
          {data.customMessage && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl px-8 py-6 mb-10 text-left">
              <p className="text-yellow-800 italic leading-relaxed">"{data.customMessage}"</p>
            </div>
          )}

          {/* gallery */}
          {gallery.length > 0 && (
            <div className="flex gap-3 mb-10 overflow-hidden">
              {gallery.slice(0, 4).map((url, i) => (
                <div key={i} className="flex-1 aspect-square rounded-xl overflow-hidden">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          {/* RSVP */}
          <div style={{ background: 'rgba(124,58,237,0.88)', borderRadius: 20, padding: '24px 20px' }}>
            <RsvpForm invitationId={invitationId} isLive={isLive} externalLink={data.rsvpLink || null} />
          </div>
        </div>

        <div className="flex h-4">
          {[...DOTS].reverse().map((cls, i) => (
            <div key={i} className={`flex-1 ${cls}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

registry.register({ metadata: META, component: BirthdayFun });
